import { ExitToApp } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { ReactElement } from "react";

import { ListSubaccountsQuery } from "~frontend/__generated__/graphql";
import Table, { RowAction, RowActions } from "~frontend/components/Table";
import Archive from "~frontend/components/icons/Archive";
import Unarchive from "~frontend/components/icons/Unarchive";
import {
  Subaccount,
  useCurrentUserIsStaff,
} from "~frontend/providers/LoggedInUserProvider";

export default function SubaccountTable({
  rows,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  totalCount,
  toggleArchiveSubaccount,
}: {
  rows: ListSubaccountsQuery["listSubaccounts"];
  page: number;
  handleChangePage: (newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  toggleArchiveSubaccount: (
    subaccountId: string,
    archive: boolean
  ) => Promise<void>;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();

  function getRowActions(row: Subaccount): RowActions<Subaccount> {
    const archiveAction: RowAction<Subaccount> = {
      id: "archive",
      type: "button",
      icon: <Archive />,
      tooltipLabel: "Archive",
      confirm: {
        title: "Archive Subaccount",
        description: "Are you sure you want to archive this subaccount?",
      },
      async onClick() {
        await toggleArchiveSubaccount(row.id, true);
      },
    };

    const unarchiveAction: RowAction<Subaccount> = {
      id: "unarchive",
      type: "button",
      icon: <Unarchive />,
      tooltipLabel: "Unarchive",
      confirm: {
        title: "Unarchive Subaccount",
        description: "Are you sure you want to reinstate this subaccount?",
      },
      async onClick() {
        await toggleArchiveSubaccount(row.id, false);
      },
    };

    const goToSubaccountAction: RowAction<Subaccount> = {
      id: "go-to-subaccount",
      type: "link",
      icon: <ExitToApp />,
      tooltipLabel: "Go To Subaccount",
      href: (subaccount: Subaccount) =>
        `/subaccounts/${subaccount.slug}/campaigns`,
    };

    if (isStaff) {
      return row.archivedAt
        ? [unarchiveAction, goToSubaccountAction]
        : [archiveAction, goToSubaccountAction];
    }

    return [goToSubaccountAction];
  }

  return (
    <Table
      columns={[
        {
          key: "name",
          title: "Name",
          renderValue: subaccount => (
            <>
              <Typography data-testid="subaccount-name-cell">
                {subaccount.name}
                {subaccount.archivedAt ? " (ARCHIVED)" : ""}
              </Typography>
              <Typography variant="body3">{subaccount.id}</Typography>
            </>
          ),
        },
        {
          key: "createdAt",
          title: "Created At",
          renderValue: subaccount =>
            new Date(subaccount.createdAt).toLocaleString(),
        },
      ]}
      rows={rows}
      rowActions={getRowActions}
      emptyState="No subaccounts found"
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      page={page}
      onChangePage={handleChangePage}
    />
  );
}
